import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, Grid, LinearProgress, Paper,
  Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography, Breadcrumbs,
} from '@mui/material';
import {
  TollOutlined as CreditIcon, AddCircleOutline as GiveIcon,
  RemoveCircleOutline as TakeIcon, Domain as EnterpriseIcon,
  AccountTree as OrgIcon, Map as RegionIcon, Business as OfficeIcon,
  AddBusiness as NewOrgIcon,
} from '@mui/icons-material';
import * as hierarchyApi from '../api/hierarchy';
import CreditHistory from '../components/CreditHistory';
import CreditRequestsPanel from '../components/CreditRequestsPanel';

/**
 * Distributing AI sessions, for a session scoped to a LEVEL rather than a clinic.
 *
 * One screen for all three levels, because all three do the same job: hand your pool
 * down to the children one hop below. The level comes from the session, so nothing here
 * is written per-role — the only branch is which words to use, and those come from the
 * server (`node.label`, each child's `scope`).
 *
 * The structure lives on the overview and the people on their own page; this screen is
 * only the ledger.
 */

const LEVEL = {
  enterprise: { icon: <EnterpriseIcon />, color: '#7c3aed', childWord: 'organisations' },
  organization: { icon: <OrgIcon />, color: '#2563eb', childWord: 'regions and offices' },
  region: { icon: <RegionIcon />, color: '#0891b2', childWord: 'offices' },
  clinic: { icon: <OfficeIcon />, color: '#059669', childWord: '' },
};

/** A pool hands sessions down and spends none; an office spends them and hands none on. */
function balanceOf(row) {
  if (!row.has_account) return { headline: 'No pool', detail: 'Nothing granted yet', pct: null };
  if (row.unlimited) return { headline: 'Unlimited', detail: 'No cap', pct: null };
  if (row.scope === 'clinic') {
    return {
      headline: `${row.remaining ?? 0} left`,
      detail: `${row.used ?? 0} used of ${row.granted ?? 0}`,
      pct: row.granted ? Math.min(100, ((row.used ?? 0) / row.granted) * 100) : null,
    };
  }
  return {
    headline: `${row.available ?? 0} to give`,
    detail: `${row.allocated ?? 0} passed on of ${row.granted ?? 0}`,
    pct: row.granted ? Math.min(100, ((row.allocated ?? 0) / row.granted) * 100) : null,
  };
}

export default function NodeCredits() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [moving, setMoving] = useState(null);        // { child, direction }
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [newOrg, setNewOrg] = useState(null);        // { name } while the dialog is open

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await hierarchyApi.getCredits());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load this account');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitMove = async () => {
    // Whole sessions only: parseInt quietly turned 2.7 into 2 and moved that instead.
    if (!/^\d+$/.test(String(amount).trim()) || Number(amount) <= 0) {
      toast.error('Enter a whole number of sessions');
      return;
    }
    const n = Number(amount);
    setSaving(true);
    try {
      const res = await hierarchyApi.allocate(
        moving.child.scope, moving.child.id,
        moving.direction === 'give' ? n : -n,
      );
      toast.success(res.message);
      setMoving(null);
      setAmount('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not move those sessions');
    } finally {
      setSaving(false);
    }
  };

  const toggleUnlimited = async (child) => {
    setBusyId(child.id);
    try {
      await hierarchyApi.setChildUnlimited(child.scope, child.id, !child.unlimited);
      toast.success(child.unlimited
        ? `${child.name} now has a session limit`
        : `${child.name} is now unlimited`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not change that');
    } finally {
      setBusyId(null);
    }
  };

  const submitNewOrg = async () => {
    const name = (newOrg?.name || '').trim();
    if (!name) { toast.error('Give the organisation a name'); return; }
    setSaving(true);
    try {
      await hierarchyApi.createOrganization({ name });
      toast.success(`${name} created`);
      setNewOrg(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create that organisation');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  }
  if (!data) return null;

  const { node, ancestors = [], children = [], totals = {} } = data;
  const level = LEVEL[node.scope] || LEVEL.organization;
  const pool = balanceOf(node);

  return (
    <Box>
      {/* Where this level sits. Names only — an admin may see their place in the
          hierarchy without reading anything about the levels above them. */}
      {!!ancestors.length && (
        <Breadcrumbs sx={{ mb: 1 }}>
          {ancestors.map((a) => (
            <Typography key={a.id} variant="body2" color="text.secondary">{a.name}</Typography>
          ))}
          <Typography variant="body2" fontWeight={600}>{node.name}</Typography>
        </Breadcrumbs>
      )}

      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ color: level.color, display: 'flex' }}>{level.icon}</Box>
          <Box>
            <Typography variant="h4" fontWeight={700}>{node.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {/* `children` is every descendant now, not just the direct ones, so it
                  cannot be described as "organisations" or "offices" — it is a mix. */}
              Your {node.label} — {children.length} place
              {children.length === 1 ? '' : 's'} beneath it, at every level
            </Typography>
          </Box>
        </Stack>
        {node.scope === 'enterprise' && (
          <Button variant="contained" startIcon={<NewOrgIcon />}
            onClick={() => setNewOrg({ name: '' })}>
            New organisation
          </Button>
        )}
      </Stack>

      <CreditRequestsPanel onAnswered={load} />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2, borderTop: `3px solid ${level.color}` }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <CreditIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                YOUR POOL OF AI SESSIONS
              </Typography>
            </Stack>
            <Typography variant="h4" fontWeight={700}>{pool.headline}</Typography>
            <Typography variant="caption" color="text.secondary">{pool.detail}</Typography>
            {pool.pct !== null && (
              <LinearProgress variant="determinate" value={pool.pct} sx={{ mt: 1.5 }} />
            )}
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h4" fontWeight={700}>{totals.used ?? 0}</Typography>
            <Typography variant="caption" color="text.secondary">
              Sessions used, all offices beneath you
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h4" fontWeight={700}>
              {children.filter((c) => c.depth === 0).length}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Direct {level.childWord || 'children'} · {children.length} in all
              {totals.unlimited_children ? ` · ${totals.unlimited_children} unlimited` : ''}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Card>
        <CardContent sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={700}>Distribute your sessions</Typography>
          <Typography variant="body2" color="text.secondary">
            Everything beneath your {node.label}. Give sessions to any of them directly —
            an office does not have to be funded through its region. What they have
            already spent or passed on cannot be taken back, and you can only reclaim
            what you gave.
          </Typography>
        </CardContent>
        <Divider />
        {!children.length ? (
          <Box sx={{ p: 5, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Nothing beneath this {node.label} yet.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Level</TableCell>
                  <TableCell align="right">Granted</TableCell>
                  <TableCell align="right">Used / passed on</TableCell>
                  <TableCell align="right">Left</TableCell>
                  <TableCell align="center">Unlimited</TableCell>
                  <TableCell align="center">Move</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {children.map((child) => {
                  const lv = LEVEL[child.scope] || LEVEL.clinic;
                  return (
                    <TableRow key={child.id} hover
                      sx={{ opacity: child.is_active === false ? 0.55 : 1 }}>
                      {/* Indent by depth. The list is everything beneath this node, at
                          every level, because a node may fund any of them directly —
                          so the shape of the account has to be readable in the table
                          that does the funding. */}
                      <TableCell sx={{ pl: 2 + (child.depth || 0) * 3 }}>
                        <Typography variant="body2" fontWeight={child.depth ? 500 : 600}>
                          {child.name}
                        </Typography>
                        {child.is_active === false && (
                          <Chip size="small" label="Switched off"
                            sx={{ height: 18, fontSize: 10, mt: 0.4 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined"
                          label={child.scope === 'clinic' ? 'Office'
                            : child.scope === 'region' ? 'Region' : 'Organisation'}
                          sx={{ height: 20, fontSize: 11, borderColor: lv.color, color: lv.color }} />
                      </TableCell>
                      <TableCell align="right">
                        {child.unlimited ? '—' : (child.granted ?? 0)}
                      </TableCell>
                      <TableCell align="right">{child.committed ?? 0}</TableCell>
                      <TableCell align="right">
                        {child.unlimited
                          ? <Chip size="small" color="success" label="Unlimited" sx={{ height: 20 }} />
                          : (child.headroom ?? 0)}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={node.unlimited
                          ? 'Take the cap off this child'
                          : `Only available while your ${node.label} is itself unlimited`}>
                          <span>
                            <Switch
                              size="small"
                              checked={!!child.unlimited}
                              disabled={busyId === child.id || !node.unlimited}
                              onChange={() => toggleUnlimited(child)}
                            />
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Hand sessions down">
                          <span>
                            <Button size="small" startIcon={<GiveIcon />}
                              disabled={child.unlimited}
                              onClick={() => { setMoving({ child, direction: 'give' }); setAmount(''); }}>
                              Give
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip title="Take unspent sessions back">
                          <span>
                            <Button size="small" color="inherit" startIcon={<TakeIcon />}
                              disabled={child.unlimited}
                              onClick={() => { setMoving({ child, direction: 'take' }); setAmount(''); }}>
                              Take
                            </Button>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <Dialog open={Boolean(moving)} onClose={() => !saving && setMoving(null)}
        maxWidth="xs" fullWidth>
        <DialogTitle>
          {moving?.direction === 'give'
            ? `Give sessions to ${moving?.child.name}`
            : `Take sessions back from ${moving?.child.name}`}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {moving?.direction === 'give'
              ? `You have ${node.unlimited ? 'an unlimited pool' : `${node.available ?? 0} left to give`}.`
              : 'Only what you gave them, and only what they have not spent or passed on.'}
          </Typography>
          <TextField autoFocus fullWidth type="number" label="Sessions" value={amount}
            onChange={(e) => setAmount(e.target.value)} inputProps={{ min: 1 }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setMoving(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={submitMove} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
            {moving?.direction === 'give' ? 'Give' : 'Take back'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(newOrg)} onClose={() => !saving && setNewOrg(null)}
        maxWidth="xs" fullWidth>
        <DialogTitle>New organisation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            It starts empty. Appoint its administrator and they open its first sites.
          </Typography>
          <TextField autoFocus fullWidth label="Organisation name" value={newOrg?.name || ''}
            onChange={(e) => setNewOrg({ name: e.target.value })} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNewOrg(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={submitNewOrg} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
      <CreditHistory endpoint="/api/v1/hierarchy/credits/history" />
    </Box>
  );
}
